import { all, call, Effect, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actionTypes from '../actionTypes';
import {
    deviceAPI,
    getAllDeviceAPI,
    getSingleDeviceGraphAPI,
    singleDeviceAPI,
    getSingleDeviceCustomRangeData,
    getFanSpeedConfigurationAPI,
    updateDeviceInstallationConfigurationAPI,
    updateDeviceInstallationConfigurationForSuperAdminAPI,
} from 'api/deviceAPI';
import {
    fetchDeviceAppendSuccess,
    fetchDeviceFailure,
    fetchDeviceSuccess,
    fetchSingleDeviceFailure,
    fetchSingleDeviceGraphFailure,
    fetchSingleDeviceGraphSuccess,
    fetchSingleDeviceSuccess,
    getAllDevice,
    getAllDeviceFailure,
    getAllDeviceSucess,
    getFanSpeedConfigurationFailure,
    getFanSpeedConfigurationSuccess,
    updateDeviceInstallationConfigurationFailure,
    updateDeviceInstallationConfigurationForSuperAdminFailure,
    updateDeviceInstallationConfigurationForSuperAdminSuccess,
    updateDeviceInstallationConfigurationSuccess,
} from 'redux/actions/deviceActions';
import { DeviceResponse, GetAllDeviceResponse, GetFanSpeedConfigurationResponse } from 'redux/types';
import { GraphResponse, ThingData } from 'shared/model/DeviceModel';
import { saveDeviceFilter } from 'redux/actions/deviceFilterActions';
import { setToastAction } from 'redux/actions';
import { ToastVariant } from 'shared/utils/Constants';
import { fetchSiteHeirarchyRequest } from 'redux/actions/siteActions';

/*
  Worker Saga: Fired on DEVICE_REQUEST action
*/
function* fetchDeviceSaga(action: Effect) {
    try {
        const isMetalmarkQuery = action.payload.isMetalmarkQuery;
        const response: DeviceResponse = yield call(deviceAPI, action.payload);
        yield put(
            fetchDeviceSuccess({
                things: response.things,
                nextToken: response.nextToken,
                count: response.count,
                isMetalmarkQuery,
            }),
        );
        yield put(saveDeviceFilter());
    } catch (e: any) {
        yield put(
            fetchDeviceFailure({
                error: e.message,
            }),
        );
    }
}

/*
  Worker Saga: Fired on DEVICE_REQUEST_APPEND action
*/
function* fetchDeviceAppendSaga(action: Effect) {
    try {
        const response: DeviceResponse = yield call(deviceAPI, action.payload);
        yield put(
            fetchDeviceAppendSuccess({
                things: response.things,
                nextToken: response.nextToken,
                count: response.count,
            }),
        );
    } catch (e: any) {
        yield put(
            fetchDeviceFailure({
                error: e.message,
            }),
        );
    }
}

/*
  Worker Saga: Fired on SINGLE_DEVICE_REQUEST action
*/
function* fetchSingleDeviceSaga(action: Effect) {
    try {
        const response: ThingData = yield call(singleDeviceAPI, action.payload.id);
        yield put(
            fetchSingleDeviceSuccess({
                thing: response,
            }),
        );
    } catch (e: any) {
        yield put(
            fetchSingleDeviceFailure({
                error: e.message,
            }),
        );
    }
}

/*
  Worker Saga: Fired on SINGLE_DEVICE_GRAPH_REQUEST action
*/
function* fetchSingleDeviceGraphSaga(action: Effect) {
    try {
        const response: GraphResponse = yield call(
            getSingleDeviceGraphAPI,
            action.payload.id,
            action.payload.timestamp,
        );
        yield put(
            fetchSingleDeviceGraphSuccess({
                graphResponse: response,
            }),
        );
    } catch (e: any) {
        yield put(
            fetchSingleDeviceGraphFailure({
                error: e.message,
            }),
        );
    }
}

/*
  Worker Saga: Fired on SINGLE_DEVICE_CUSTOM_RANGE_REQUEST action
*/
function* fetchSingleDeviceCustomRangeSaga(action: Effect) {
    try {
        const response: GraphResponse = yield call(
            getSingleDeviceCustomRangeData,
            action.payload.id,
            action.payload.startTime,
            action.payload.endTime,
        );
        yield put(
            fetchSingleDeviceGraphSuccess({
                graphResponse: response,
            }),
        );
    } catch (e: any) {
        yield put(
            fetchSingleDeviceGraphFailure({
                error: e.message,
            }),
        );
    }
}

/*
  Worker Saga: Fired on GET_ALL_DEVICES action
*/
function* getAllDeviceSaga(action: Effect) {
    try {
        const { orgId } = action.payload;
        const response: GetAllDeviceResponse = yield call(getAllDeviceAPI, orgId);
        yield put(
            getAllDeviceSucess({
                things: response.things,
                count: 0,
            }),
        );
    } catch (e: any) {
        yield put(
            getAllDeviceFailure({
                error: e.message,
            }),
        );
    }
}

function* getFanSpeedConfigurationSaga(action: Effect) {
    try {
        const response: GetFanSpeedConfigurationResponse = yield call(getFanSpeedConfigurationAPI);
        yield put(
            getFanSpeedConfigurationSuccess(response),
        );
    } catch (e: any) {
        yield put(
            getFanSpeedConfigurationFailure({
                error: e.message,
            }),
        );
    }
}

function* updateDeviceInstallationConfigurationSaga(action: Effect) {
    try {
        const { device_id, fan_speed_adjustment_id, org_id ,isCustomer } = action.payload;
        const body = {
            device_id,
            fan_speed_adjustment_id
        }
        const response: string = yield call(updateDeviceInstallationConfigurationAPI, body);

        yield put(updateDeviceInstallationConfigurationSuccess(response));
        yield put(isCustomer ? getAllDevice(org_id) : getAllDevice());
        yield put(fetchSiteHeirarchyRequest(org_id));
        yield put(setToastAction(ToastVariant.SUCCESS, response));
    } catch (e: any) {
        yield put(updateDeviceInstallationConfigurationFailure({ error: e.message }));
    }
}

function* updateDeviceInstallationConfigurationForSuperAdminSaga(action: Effect) {
    try {
        const { option, isCustomer, device_id, org_id, installation_configuration } = action.payload;
        const response: string = yield call(updateDeviceInstallationConfigurationForSuperAdminAPI, option, device_id, installation_configuration);
        yield put(updateDeviceInstallationConfigurationForSuperAdminSuccess(response));
        yield put(isCustomer ? getAllDevice(org_id) : getAllDevice());
        yield put(fetchSiteHeirarchyRequest(org_id));
        yield put(setToastAction(ToastVariant.SUCCESS, response));
    }
    catch (e: any) {
        yield put(updateDeviceInstallationConfigurationForSuperAdminFailure({ error: e.message }));
    }
}

function* deviceSaga() {
    yield all([takeEvery(actionTypes.DEVICE_REQUEST, fetchDeviceSaga)]);
    yield all([takeLatest(actionTypes.DEVICE_REQUEST_APPEND, fetchDeviceAppendSaga)]);
    yield all([takeLatest(actionTypes.SINGLE_DEVICE_REQUEST, fetchSingleDeviceSaga)]);
    yield all([takeLatest(actionTypes.SINGLE_DEVICE_GRAPH_REQUEST, fetchSingleDeviceGraphSaga)]);
    yield all([takeLatest(actionTypes.GET_ALL_DEVICES, getAllDeviceSaga)]);
    yield all([takeLatest(actionTypes.SINGLE_DEVICE_CUSTOM_RANGE_REQUEST, fetchSingleDeviceCustomRangeSaga)]);
    yield all([takeLatest(actionTypes.GET_FAN_SPEED_CONFIGURATION, getFanSpeedConfigurationSaga)]);
    yield all([takeLatest(actionTypes.UPDATE_DEVICE_INSTALLATION_CONFIGURATION, updateDeviceInstallationConfigurationSaga)]);
    yield all([takeLatest(actionTypes.UPDATE_DEVICE_INSTALLATION_CONFIGURATION_FOR_SUPER_ADMIN, updateDeviceInstallationConfigurationForSuperAdminSaga)]);
}

export default deviceSaga;
